import { i18next, Trans } from '@frontend/i18n';
import { UseFormReturnType, Text } from '@frontend/design-system';
import { AiIconChip } from './atoms';

export type AvailabilityMode = 'OPERATORY' | 'PROVIDER';
export type BookableEntityBucketType = 'DISPLAY' | 'WRITEBACK' | 'BOOKING';

export const availabilityRecurrenceOptions = [
  { id: 'WEEKLY', displayName: i18next.t('Weekly', { ns: 'scheduleSettings' }) },
  { id: 'SPECIFIC_DATE', displayName: i18next.t('Specific Date', { ns: 'scheduleSettings' }) },
  { id: 'CUSTOM', displayName: i18next.t('Custom', { ns: 'scheduleSettings' }) },
] as const;

export const patientBookingOptions = [
  {
    id: 'DIRECT',
    title: (
      <div css={{ display: 'flex', justifyContent: 'space-between' }}>
        <Text size='medium'>{i18next.t('Self-Bookable', { ns: 'scheduleSettings' })}</Text>
        <AiIconChip iconName='ai-regenerate-small'>{i18next.t('AI can see', { ns: 'scheduleSettings' })}</AiIconChip>
      </div>
    ),
    description: i18next.t('Patients can book instantly online or via the AI assistant. No staff approval required.', {
      ns: 'scheduleSettings',
    }),
  },
  {
    id: 'REQUEST',
    title: i18next.t('Request Only (needs confirmation)', { ns: 'scheduleSettings' }),
    description: i18next.t('Requests can be made via the booking portal and require staff approval.', {
      ns: 'scheduleSettings',
    }),
  },
  {
    id: 'INTERNAL',
    title: i18next.t('Not Available', { ns: 'scheduleSettings' }),
    description: i18next.t('Hidden from patient booking. For staff use only.', { ns: 'scheduleSettings' }),
  },
] as const;

export const bookingCadenceOptions = [
  {
    id: 'none',
    displayName: i18next.t('None', { ns: 'scheduleSettings' }),
    description: (
      <Trans i18next={i18next} ns='scheduleSettings'>
        Continuous booking (no set intervals) Start times fill gaps as previous appointments end: 8:30 AM, 9:15 AM,
        10:00 AM...
      </Trans>
    ),
  },
  {
    id: '0',
    displayName: i18next.t('Top of the hour', { ns: 'scheduleSettings' }),
    description: (
      <Trans i18next={i18next} ns='scheduleSettings'>
        Slots at the top of each hour (on the hour)
        <br />
        Available start times: 9:00 AM, 10:00 AM, 11:00 AM...
      </Trans>
    ),
  },
  {
    id: '60',
    displayName: i18next.t('Every hour', { ns: 'scheduleSettings' }),
    description: (
      <Trans i18next={i18next} ns='scheduleSettings'>
        Slots every hour from 8:30 AM - 11:30 AM
        <br />
        Available start times: 8:30 AM, 9:30 AM, 10:30 AM
      </Trans>
    ),
  },
  {
    id: '10',
    displayName: i18next.t('Every 10 minutes', { ns: 'scheduleSettings' }),
    description: (
      <Trans i18next={i18next} ns='scheduleSettings'>
        Slots every 10 minutes from 8:30 AM - 11:30 AM
        <br />
        Available start times: 8:30 AM, 8:40 AM, 8:50 AM, 9:00 AM
      </Trans>
    ),
  },
  {
    id: '15',
    displayName: i18next.t('Every 15 minutes', { ns: 'scheduleSettings' }),
    description: (
      <Trans i18next={i18next} ns='scheduleSettings'>
        Slots every 15 minutes from 8:30 AM - 11:30 AM
        <br />
        Available start times: 8:30 AM, 8:45 AM, 9:00 AM, 9:15 AM
      </Trans>
    ),
  },
  {
    id: '20',
    displayName: i18next.t('Every 20 minutes', { ns: 'scheduleSettings' }),
    description: (
      <Trans i18next={i18next} ns='scheduleSettings'>
        Slots every 20 minutes from 8:30 AM - 11:30 AM
        <br />
        Available start times: 8:30 AM, 8:50 AM, 9:10 AM, 9:30 AM
      </Trans>
    ),
  },
  {
    id: '30',
    displayName: i18next.t('Every 30 minutes', { ns: 'scheduleSettings' }),
    description: (
      <Trans i18next={i18next} ns='scheduleSettings'>
        Slots every 30 minutes from 8:30 AM - 11:30 AM
        <br />
        Available start times: 8:30 AM, 9:00 AM, 9:30 AM, 10:00 AM
      </Trans>
    ),
  },
] as const;

export const repeatFrequencyOptions = [
  { id: 'days', displayName: i18next.t('Days', { ns: 'scheduleSettings' }) },
  { id: 'weeks', displayName: i18next.t('Weeks', { ns: 'scheduleSettings' }) },
  { id: 'months', displayName: i18next.t('Months', { ns: 'scheduleSettings' }) },
] as const;

export const monthlyWeekDayOptions = [
  { id: 'monday', displayName: i18next.t('Monday', { ns: 'scheduleSettings' }) },
  { id: 'tuesday', displayName: i18next.t('Tuesday', { ns: 'scheduleSettings' }) },
  { id: 'wednesday', displayName: i18next.t('Wednesday', { ns: 'scheduleSettings' }) },
  { id: 'thursday', displayName: i18next.t('Thursday', { ns: 'scheduleSettings' }) },
  { id: 'friday', displayName: i18next.t('Friday', { ns: 'scheduleSettings' }) },
  { id: 'saturday', displayName: i18next.t('Saturday', { ns: 'scheduleSettings' }) },
  { id: 'sunday', displayName: i18next.t('Sunday', { ns: 'scheduleSettings' }) },
] as const;

export type AvailabilityRecurrenceTypes = (typeof availabilityRecurrenceOptions)[number]['id'];
export type PatientBookingOptions = (typeof patientBookingOptions)[number]['id'];
export type BookingCadenceOptions = (typeof bookingCadenceOptions)[number]['id'];
export type RepeatFrequencyOptions = (typeof repeatFrequencyOptions)[number]['id'];
export type MonthlyFrequencyTypeOptions = 'day' | 'weekday';
export type MonthlyWeekDayOptions = (typeof monthlyWeekDayOptions)[number]['id'];

export type AvailabilityRuleFormConfig = {
  operatory: { type: 'dropdown'; value: string; required: boolean };
  serviceableProvider: { type: 'dropdown'; value: string; required: boolean };
  billableProvider: { type: 'dropdown'; value: string; required: boolean };
  displayName: { type: 'text'; value: string; required: boolean };
  recurrenceType: { type: 'dropdown'; value: AvailabilityRecurrenceTypes; required: boolean };
  services: { type: 'checklist'; value: string[]; required: boolean };
  bookingCadence: { type: 'dropdown'; value: BookingCadenceOptions; required: boolean };
  patientBooking: { type: 'radio'; value: PatientBookingOptions | ''; required: boolean };
  // MM/DD/YYYY format for date fields to be compatible with the date input value format
  startDate: { type: 'date'; value: string; required: boolean };
  endDate: { type: 'date'; value: string };
  // HH:mm format for time fields to be compatible with the time input value format
  startTime: { type: 'time'; value: string; required: boolean };
  endTime: { type: 'time'; value: string; required: boolean };
};

export type useAvailabilityRuleFormReturnType = UseFormReturnType<
  AvailabilityRuleFormConfig,
  keyof AvailabilityRuleFormConfig
>;
export type GetAvailabilityRuleFormFieldProps = useAvailabilityRuleFormReturnType['getFieldProps'];
export type AvailabilityRuleFormValues = useAvailabilityRuleFormReturnType['values'];

export type AvailabilityRuleFormFieldKeys = keyof AvailabilityRuleFormConfig;

export type RuleRecurrenceFormConfig = {
  repeatFrequency: { type: 'dropdown'; value: string; required: boolean };
  repeatFrequencyType: { type: 'dropdown'; value: RepeatFrequencyOptions; required: boolean };
};

export type MonthlyRecurrenceFormConfig = {
  monthlyFrequencyType: { type: 'radio'; value: MonthlyFrequencyTypeOptions; required: boolean };
  monthlyFrequencyDay: { type: 'dropdown'; value: string };
  monthlyFrequencyWeekday: { type: 'dropdown'; value: MonthlyWeekDayOptions };
  monthlyFrequencyWeek: { type: 'dropdown'; value: string };
};
export type RuleRecurrenceFormReturnType = UseFormReturnType<RuleRecurrenceFormConfig, keyof RuleRecurrenceFormConfig>;
export type RuleRecurrenceFormValues = RuleRecurrenceFormReturnType['values'];
export type GetRuleRecurrenceFormFieldProps = RuleRecurrenceFormReturnType['getFieldProps'];

export type MonthlyRecurrenceFormReturnType = UseFormReturnType<
  MonthlyRecurrenceFormConfig,
  keyof MonthlyRecurrenceFormConfig
>;
export type MonthlyRecurrenceFormValues = MonthlyRecurrenceFormReturnType['values'];
export type GetMonthlyRecurrenceFormFieldProps = MonthlyRecurrenceFormReturnType['getFieldProps'];
